import { FullScreenPass } from "@faro-lotv/lotv";
import {
  Camera,
  Color,
  Matrix4,
  NoBlending,
  ShaderMaterial,
  Uniform,
  type Texture,
  type WebGLRenderer,
  type WebGLRenderTarget,
} from "three";
import type { PipeSelectionGpu } from "./pipe-selection-gpu";

export const PIPE_RUN_COLOR = "#ffd04a";

/** Highlights observed pipe surfaces using the same lookup as opacity/isolation. */
export class PipeSelectionPass extends FullScreenPass<ShaderMaterial> {
  private readonly uniforms = {
    uColor: new Uniform<Texture | null>(null),
    uDepth: new Uniform<Texture | null>(null),
    uInverse: new Uniform(new Matrix4()),
    uPipeTint: new Uniform(new Color(PIPE_RUN_COLOR)),
  };

  /**
   * @param camera Active pipe view.
   * @param mask Borrowed shared membership lookup; its owner disposes it.
   */
  constructor(
    readonly camera: Camera,
    mask: PipeSelectionGpu,
  ) {
    super(
      new ShaderMaterial({
        blending: NoBlending,
        vertexShader:
          "varying vec2 vUv; void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }",
        fragmentShader: `precision highp float;
uniform sampler2D uColor;
uniform sampler2D uDepth;
uniform mat4 uInverse;
uniform vec3 uPipeTint;
varying vec2 vUv;
${mask.shader}
void main() {
  float depth = texture2D(uDepth, vUv).r;
  vec4 color = texture2D(uColor, vUv);
  gl_FragDepth = depth;
  if (depth < 1.0 && color.a > 0.0) {
    vec4 world = uInverse * vec4(vUv * 2.0 - 1.0, depth * 2.0 - 1.0, 1.0);
    if (selectedSurfacePoint(world)) color.rgb = mix(color.rgb, uPipeTint, 0.65);
  }
  gl_FragColor = color;
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}`,
      }),
    );
    this.material.uniforms = { ...this.uniforms, ...mask.uniforms };
  }

  /**
   * @param renderer Active renderer.
   * @param output Destination.
   * @param input Shaded point color/depth.
   */
  render(
    renderer: WebGLRenderer,
    output: WebGLRenderTarget,
    input: WebGLRenderTarget,
  ): void {
    this.uniforms.uColor.value = input.texture;
    this.uniforms.uDepth.value = input.depthTexture;
    this.uniforms.uInverse.value.multiplyMatrices(
      this.camera.matrixWorld,
      this.camera.projectionMatrixInverse,
    );
    renderer.setRenderTarget(this.renderToScreen ? null : output);
    renderer.clear();
    this.fsQuad.render(renderer);
  }
}
